import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalInvestment } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { StandardButton } from '../ui';

interface GlobalInvestmentSelectorProps {
  selectedInvestmentIds: string[];
  onChange: (selectedIds: string[]) => void;
  maxSelections?: number;
}

const GlobalInvestmentSelector: React.FC<GlobalInvestmentSelectorProps> = ({
  selectedInvestmentIds,
  onChange,
  maxSelections = 10
}) => {
  const { isLight } = useTheme();
  const [globalInvestments, setGlobalInvestments] = useState<GlobalInvestment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadGlobalInvestments();
  }, []);

  const loadGlobalInvestments = async () => {
    try {
      setLoading(true);
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (data?.globalInvestments) {
        const investments = data.globalInvestments
          .map(([id, investment]) => investment)
          .filter(inv => inv.isEnabled);
        setGlobalInvestments(investments);
      } else {
        setGlobalInvestments([]);
      }
    } catch (err) {
      console.error('Failed to load global investments:', err);
      setError('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleInvestment = (investmentId: string) => {
    const isSelected = selectedInvestmentIds.includes(investmentId);
    
    if (isSelected) {
      // Remove from selection
      onChange(selectedInvestmentIds.filter(id => id !== investmentId));
    } else {
      // Add to selection if under limit
      if (selectedInvestmentIds.length < maxSelections) {
        onChange([...selectedInvestmentIds, investmentId]);
      }
    }
  };

  const filteredInvestments = globalInvestments.filter(investment => {
    const matchesSearch = investment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investment.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || investment.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(globalInvestments.map(inv => inv.category)));

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'education': return '🎓';
      case 'infrastructure': return '🏗️';
      case 'technology': return '💻';
      case 'defense': return '🛡️';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-professional-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <StandardButton
          onClick={loadGlobalInvestments}
          variant="primary"
        >
          Retry
        </StandardButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`}>
            Select Investments
          </h3>
          <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            {selectedInvestmentIds.length} of {maxSelections} selected
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search investments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border ${
              isLight 
                ? 'border-slate-300 bg-white text-slate-900 placeholder-slate-500' 
                : 'border-white/20 bg-white/5 text-white placeholder-slate-400'
            } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${
            isLight 
              ? 'border-slate-300 bg-white text-slate-900' 
              : 'border-white/20 bg-white/5 text-white'
          } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>
              {getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Investment Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        layout
      >
        <AnimatePresence>
          {filteredInvestments.map((investment) => {
            const isSelected = selectedInvestmentIds.includes(investment.id);
            const canSelect = isSelected || selectedInvestmentIds.length < maxSelections;

            return (
              <motion.div
                key={investment.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? isLight
                      ? 'bg-professional-blue-50 border-professional-blue-300 shadow-md'
                      : 'bg-professional-blue-500/20 border-professional-blue-400 shadow-md'
                    : canSelect
                    ? isLight
                      ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
                    : isLight
                    ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                    : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                }`}
                onClick={() => canSelect && handleToggleInvestment(investment.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-2xl">{investment.icon}</div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-professional-blue-600 border-professional-blue-600'
                      : isLight
                      ? 'border-slate-300'
                      : 'border-white/30'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                <h4 className={`font-semibold mb-2 ${
                  isLight ? 'text-slate-800' : 'text-white'
                }`}>
                  {investment.name}
                </h4>

                <p className={`text-sm mb-3 line-clamp-2 ${
                  isLight ? 'text-slate-600' : 'text-slate-300'
                }`}>
                  {investment.description}
                </p>

                <div className="flex items-center justify-between">
                  <span className={`text-sm px-2 py-1 rounded ${
                    isLight
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-white/10 text-slate-300'
                  }`}>
                    {getCategoryIcon(investment.category)} {investment.category}
                  </span>
                  <span className={`text-sm font-medium ${
                    isLight ? 'text-slate-700' : 'text-slate-200'
                  }`}>
                    ${(investment.cost / 1000).toFixed(0)}K
                  </span>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {filteredInvestments.length === 0 && (
        <div className="text-center py-12">
          <p className={`text-lg ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            No investments found matching your criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalInvestmentSelector;